import React, { useEffect, useRef } from 'react';
import OptionEdit, { DraggableOption } from '../../SelectSettingsEdit/OptionEdit';
import CategoryColorOption from './CategoryColorOption';
import { IColorOption } from '../../../../entities/common';

type Props = {
    index: number;
    option: DraggableOption<IColorOption>;
    categoryIconName: string;
    isInEditMode: boolean;
    defaultRequired: boolean;
    isUnique: () => boolean;
    onEnterEditMode: () => void;
    onCancel: (initialOption: DraggableOption<IColorOption>) => void;
    onChange: (option: DraggableOption<IColorOption>) => void;
    onEditComplete: () => void;
    onRemove?: (optionId: string) => void;
};

const CategoryColorOptionEdit = (props: Props) => {
    const { index, defaultRequired, option, isInEditMode, categoryIconName, onChange, onEnterEditMode, onRemove } = props;
    
    const isUnique = props.isUnique();
    
    const initialOption = useRef<DraggableOption<IColorOption>>();
    useEffect(() => {
        initialOption.current = option;
    }, []);

    const onCancelWithInitialValue = () => {
        initialOption.current && props.onCancel?.(initialOption.current);
    };

    const onCancel = (e: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        onCancelWithInitialValue();
        e.stopPropagation();
        e.preventDefault();
    };
    
    const isValidOption = !!option.name.trim() && isUnique;

    const onEditComplete = (e: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        props.onEditComplete?.();
        initialOption.current = option;
        e.stopPropagation();
        e.preventDefault();
    };

    const onEditCompleteIfValid = (e: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (isValidOption) {
            onEditComplete(e);
        }
    };

    const onBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const clickedOnColorCalloutOrCancelButton = hasColorCalloutOrCancelButtonAncestor(e.relatedTarget as HTMLElement);
        if (clickedOnColorCalloutOrCancelButton) {
            // Clicked on the color callout or cancel button, don't blur
            e.preventDefault();
        }
        else if (isValidOption) {
            onEditComplete(e);
        } else {
            onCancel(e);
        }
    }

    return (
        <div className="cb-wrap">
            {!isInEditMode && (
                <CategoryColorOption
                    isDefault={defaultRequired ? index === 0 : undefined}
                    option={option}
                    iconName={categoryIconName}
                    onEditClick={onEnterEditMode}
                    onRemove={onRemove}
                />
            )}
            {isInEditMode && (
                <OptionEdit
                    option={option}
                    index={index}
                    isUnique={isUnique}
                    hideRemoveColorButton
                    onChange={onChange}
                    onRemove={onCancelWithInitialValue}
                    onBlur={onBlur}
                    cancelButtonTitle="Cancel"
                    textFieldProps={{
                        onKeyDown: e => { if (e.key === 'Escape') { onCancel(e); } },
                        onKeyPress: e => { if (e.key === 'Enter') { onEditCompleteIfValid(e); }
                    }}}
                />
            )}
        </div>
    );
};

export default CategoryColorOptionEdit;

const hasColorCalloutOrCancelButtonAncestor = (element: HTMLElement | null): boolean => {
    if (!element) {
        return false;
    }
    if (element.classList.contains("ppmx-color-picker-icon") 
        || element.classList.contains("select-option-color-callout")
        || element.classList.contains("cancel-option-edit-btn")
    ) {
        return true;
    }
    return hasColorCalloutOrCancelButtonAncestor(element.parentElement);
};
